import axios from "axios";
import {
  API_ADD_ADDITIONAL_SERVICE,
  API_DELETE_ADDITIONAL_SERVICE,
  API_GET_ADDITIONAL_SERVICE_DETAILS,
  API_GET_ADDITIONAL_SERVICES_LIST,
  API_UPDATE_ADDITIONAL_SERVICE,
} from "../Util/URLConstants";

const isSuccess = (data) => Number(data?.status_code) === 200;

const toResult = (data, list) => ({
  success: isSuccess(data),
  message: data?.message,
  data: list !== undefined ? list : data?.data ?? null,
  raw: data,
});

export const getEventSubVenueId = (event = {}) =>
  event.event_subvenue_id ||
  event.sub_venue_id ||
  event.event_sub_venue?.sub_venue_id ||
  event.eventDetails?.sub_venue_id ||
  event.eventDetails?.sub_venue?.sub_venue_id ||
  "";

export const toServiceDetails = (service = {}) => ({
  service_id: service.service_id ?? service.id,
  service_name: service.service_name,
  service_charge: Number(service.service_charge) || 0,
});

const withSubVenue = (service, parent = {}) => ({
  ...service,
  sub_venue_id:
    service.sub_venue_id ??
    parent.sub_venue_id ??
    parent.sub_venue?.sub_venue_id,
  sub_venue_name:
    service.sub_venue_name ??
    parent.sub_venue_name ??
    parent.sub_venue?.sub_venue_name,
});

export const normalizeServicesList = (payload) => {
  if (!payload) return [];

  if (Array.isArray(payload)) {
    return payload.flatMap((item) => {
      if (!item || typeof item !== "object") return [];
      if (Array.isArray(item.services)) {
        return item.services.map((service) => withSubVenue(service, item));
      }
      return [item];
    });
  }

  if (typeof payload !== "object") return [];

  if (Array.isArray(payload.services)) {
    return normalizeServicesList(payload.services);
  }
  if (Array.isArray(payload.data)) {
    return normalizeServicesList(payload.data);
  }

  const values = Object.values(payload);
  if (
    values.length > 0 &&
    values.every(
      (value) =>
        Array.isArray(value) || (value && Array.isArray(value.services))
    )
  ) {
    return normalizeServicesList(values);
  }

  return [];
};

export const groupServicesBySubVenue = (services = []) =>
  services.reduce((acc, service) => {
    const key = String(service?.sub_venue_id ?? getEventSubVenueId(service) ?? "");
    if (!acc[key]) acc[key] = [];
    acc[key].push(service);
    return acc;
  }, {});

export const applyCatalogServicesToEvents = (events = [], groupedCatalog = {}) => {
  if (!Array.isArray(events)) return [];

  return events.map((event) => {
    const existing = Array.isArray(event.service_details)
      ? event.service_details.filter((item) => item?.service_name)
      : [];
    if (existing.length > 0) {
      return {
        ...event,
        service_details: existing.map((item) => ({
          ...item,
          ...toServiceDetails(item),
        })),
      };
    }

    const catalog = groupedCatalog[String(getEventSubVenueId(event) || "")] || [];
    return {
      ...event,
      service_details: catalog.map(toServiceDetails),
    };
  });
};

export const getServicesList = async ({ user_id, sub_venue_id } = {}) => {
  const params = { user_id };
  if (sub_venue_id) params.sub_venue_id = sub_venue_id;
  const { data } = await axios.get(API_GET_ADDITIONAL_SERVICES_LIST, { params });
  return toResult(data, normalizeServicesList(data?.data ?? data));
};

export const addService = async (payload) => {
  const { data } = await axios.post(API_ADD_ADDITIONAL_SERVICE, payload);
  return toResult(data);
};

export const updateService = async (payload) => {
  const { data } = await axios.post(API_UPDATE_ADDITIONAL_SERVICE, payload);
  return toResult(data);
};

export const deleteService = async ({ id, user_id } = {}) => {
  const { data } = await axios.get(API_DELETE_ADDITIONAL_SERVICE, {
    params: { id, user_id },
  });
  return toResult(data);
};

export const getServiceDetails = async ({ id, user_id } = {}) => {
  const { data } = await axios.get(API_GET_ADDITIONAL_SERVICE_DETAILS, {
    params: { id, user_id },
  });
  return toResult(data);
};
